import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { Button, Grid, Header, Message } from 'semantic-ui-react';
import useMediaUpload from '../../../hooks/useMediaUpload';
import { FormikInput, FormikTextArea, FormikSelect, FormikField } from '../../../pages/subPages/voiceActor/FormComponents';
import { FormChangeDetector, FormikImageUploadAdapted } from '../../../bookWorksheet/components/common';
import { allStates } from '../../../constants/config';
import { useActorZone } from '../../hooks/core/useZoneHooks';
import * as Yup from 'yup';
import '../../styles/voiceActorWorksheet.css';

// Validation schema for actor profile
const actorValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  firstName: Yup.string(),
  lastName: Yup.string(),
  street: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  zipCode: Yup.string(),
  phones: Yup.string(),
  description: Yup.string()
});

/**
 * ActorProfileForm component
 * Form for creating and editing voice actor profiles
 * @param {Object} props - Component props
 * @param {Function} props.onSave - Callback when the form is saved
 * @param {Object} props.selectedActorProfile - The selected actor profile data
 * @param {string} props.actorId - The selected actor ID
 * @returns {JSX.Element} ActorProfileForm component
 */
const ActorProfileForm = ({ onSave, selectedActorProfile, actorId }) => {
  const actorZone = useActorZone();
  const { actions: actorActions } = actorZone;

  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [formChanged, setFormChanged] = useState(false);
  const formikRef = useRef();
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.data?.userId || 'default-user-id';

  const { uploadMedia, isUploading: isAvatarUploading, error: avatarUploadError, reset: resetUpload } = useMediaUpload();

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Form submission handler
  const handleSubmit = async (values) => {
    setFormLoading(true);
    setErrorMessage(null);
    setSuccessMessage('');
    console.log('[VA_ACTOR_FORM] Form values before submission:', values);

    try {
      let result;
      let updatedValues = { ...values };

      // If we have a new image file, upload it first
      if (values.hasNewImageFile && values.profileImageFile) {
        try {
          console.log('[VA_ACTOR_FORM] Uploading new image file...');
          const uploadUrl = await uploadMedia(
            values.profileImageFile,
            'voiceActorProfileImage',
            {
              actorId: actorId,
              resourceType: 'voiceActorProfileImage'
            },
            userId
          );

          console.log('[VA_ACTOR_FORM] Image uploaded successfully, URL:', uploadUrl);
          updatedValues.profilePictureUrl = uploadUrl;
        } catch (err) {
          console.error('[VA_ACTOR_FORM] Error uploading image:', err);
          setErrorMessage('Failed to upload image: ' + err.message);
          setFormLoading(false);
          return;
        }
      }

      if (actorId) {
        // Update existing actor
        console.log('[VA_ACTOR_FORM] Updating with values:', updatedValues);
        result = await actorActions.updateActor({
          actorId,
          data: updatedValues
        });
        setSuccessMessage('Voice actor updated successfully!');
      } else {
        // Create new actor
        result = await actorActions.createActor(updatedValues);
        setSuccessMessage('Voice actor created successfully!');
      }

      setFormChanged(false);

      // Call the onSave callback if provided
      if (onSave) {
        onSave(result);
      }
    } catch (err) {
      console.error('[VA_ACTOR_FORM] Error saving actor:', err);
      setErrorMessage(err.message || 'An error occurred while saving the voice actor');
    } finally {
      setFormLoading(false);
    }
  };

  // Get initial values for the form
  const getFormValues = useCallback((data) => {
    return {
      // Basic actor fields
      name: data?.name || '',
      actorName: data?.actorName || '',
      email: data?.email || '',
      firstName: data?.firstName || '',
      lastName: data?.lastName || '',

      // Contact information
      street: data?.street || '',
      city: data?.city || '',
      state: data?.state || '',
      zipCode: data?.zipCode || data?.zipcode || '',
      phones: Array.isArray(data?.phones) ? data.phones.join(', ') : (typeof data?.phones === 'string' ? data.phones : ''),

      // Profile information
      description: data?.description || '',
      actorType: data?.actorType || 'Freelance',
      status: data?.status || 'Active',

      // Image fields
      profilePictureUrl: data?.profilePictureUrl || '',
      profileImageFile: null,
      hasNewImageFile: false
    };
  }, []);

  // Check if we have profile data
  const hasProfileData = !!selectedActorProfile;

  const formReset = useCallback((resetData) => {
    if (formikRef.current) {
      console.log('[VA_ACTOR_FORM] Resetting form with data:', resetData);
      formikRef.current.resetForm({
        values: getFormValues(resetData)
      });
      setFormChanged(false);
    }
  }, [getFormValues]);

  return (
    <div className="va-actor-profile-form">
      <Header as="h3">
        {actorId ? 'Edit Voice Actor' : 'Create New Voice Actor'}
      </Header>

      {(!actorId || hasProfileData) && (
        <Formik
          innerRef={formikRef}
          initialValues={getFormValues(selectedActorProfile)}
          validationSchema={actorValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, isValid, dirty }) => (
            <Form>
              <FormChangeDetector
                onChange={setFormChanged}
                initialValues={getFormValues(selectedActorProfile)}
              />

              <div className="va-profile-img">
                {userProfile?.data?.userId && (
                  <FormikImageUploadAdapted
                    urlFieldName="profilePictureUrl"
                    fileFieldName="profileImageFile"
                    flagFieldName="hasNewImageFile"
                    resourceType="voiceActorProfileImage"
                    label="Profile Picture"
                    hideInputFields={true}
                  />
                )}
              </div>

              <Grid divided={false}>
                <Grid.Row>
                  <Grid.Column width={6}>
                    <FormikField
                      name="actorName"
                      component={FormikInput}
                      label="Actor Name*"
                      placeholder="Enter actor name"
                      fluid
                    />
                  </Grid.Column>
                  <Grid.Column width={6} className="noLeftPad">
                    <FormikField
                      name="email"
                      component={FormikInput}
                      label="Email*"
                      placeholder="Enter email address"
                      fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <Header as="h4" dividing>Basic Information</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopBottomPad">
                  <Grid.Column width={6}>
                    <FormikField
                      name="firstName"
                      component={FormikInput}
                      label="First Name"
                      fluid
                      placeholder="First Name"
                    />
                  </Grid.Column>
                  <Grid.Column className="noLeftPad" width={6}>
                    <FormikField
                      name="lastName"
                      component={FormikInput}
                      label="Last Name"
                      fluid
                      placeholder="Last Name"
                    />
                  </Grid.Column>
                  <Grid.Column className="noLeftPad" width={4}>
                    <FormikField
                      name="actorType"
                      component={FormikSelect}
                      label="Type"
                      options={[
                        { key: 'freelance', text: 'Freelance', value: 'Freelance' },
                        { key: 'authorator', text: 'Authorator-ai', value: 'Authorator-ai' },
                        { key: 'contract', text: 'Contract', value: 'Contract' }
                      ]}
                      fluid
                      placeholder="Select Type"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <Header as="h4" dividing>Contact Information</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopBottomPad">
                  <Grid.Column width={16}>
                    <FormikField
                      name="street"
                      component={FormikInput}
                      label="Street Address"
                      fluid
                      placeholder="123 Main St"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopBottomPad">
                  <Grid.Column width={6}>
                    <FormikField
                      name="city"
                      component={FormikInput}
                      label="City"
                      fluid
                      placeholder="City"
                    />
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <FormikField
                      name="state"
                      component={FormikSelect}
                      label="State"
                      options={allStates}
                      fluid
                      placeholder="State"
                    />
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <FormikField
                      name="zipCode"
                      component={FormikInput}
                      label="Zip Code"
                      fluid
                      placeholder="12345"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopBottomPad">
                  <Grid.Column width={16}>
                    <FormikField
                      name="phones"
                      component={FormikInput}
                      label="Phone Numbers (comma-separated)"
                      fluid
                      placeholder="e.g. 555-123-4567, 555-987-6543"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <Header as="h4" dividing>Profile Information</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <FormikField
                      name="description"
                      component={FormikTextArea}
                      label="Description"
                      placeholder="Enter a description for this voice actor"
                      rows={4}
                    />
                  </Grid.Column>
                </Grid.Row>

                {/* Status messages */}
                {(isAvatarUploading || avatarUploadError || successMessage || errorMessage) && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      {isAvatarUploading && (
                        <Message info>
                          <Message.Header>Uploading...</Message.Header>
                          <p>Uploading profile image...</p>
                        </Message>
                      )}

                      {avatarUploadError && (
                        <Message negative>
                          <Message.Header>Upload Error</Message.Header>
                          <p>{avatarUploadError.userMessage || avatarUploadError.message || String(avatarUploadError)}</p>
                        </Message>
                      )}

                      {errorMessage && (
                        <Message negative>
                          <Message.Header>Error</Message.Header>
                          <p>{errorMessage}</p>
                        </Message>
                      )}

                      {successMessage && (
                        <Message positive>
                          <Message.Header>Success</Message.Header>
                          <p>{successMessage}</p>
                        </Message>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                )}

                {/* Form buttons */}
                <Grid.Row>
                  <Grid.Column width={16}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Button
                        primary
                        type="submit"
                        loading={formLoading}
                        disabled={!formChanged}
                        onClick={() => handleSubmit(formikRef.current.values)}
                      >
                        {actorId ? 'Update Actor' : 'Create Actor'}
                      </Button>

                      {formChanged && (
                        <Button
                          type="button"
                          onClick={() => formReset(selectedActorProfile)}
                        >
                          Cancel Changes
                        </Button>
                      )}
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default ActorProfileForm;
